import { Button } from "@omni/ui/components/Button/button.component";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { MAX_WISHLISTS } from "@/components/Wishlist/wishlist.config";
import { useThunkDispatch } from "@/redux/dispatcher";
import { getCustomer } from "@/redux/modules/api/data/customer/customer.selectors";
import { WishlistModel } from "@/redux/modules/api/data/wishlist/@types/wishlist.model";
import * as WishlistModule from "@/redux/modules/api/data/wishlist/wishlist.module";
import {
    APIStatus,
    createWishlist
} from "@/redux/modules/api/data/wishlist/wishlist.module";
import * as WishlistSelectors from "@/redux/modules/api/data/wishlist/wishlist.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { update } from "@/redux/modules/value/wishlistSidebar/wishlistSidebar.module";
import { getCurrentListId } from "@/redux/modules/value/wishlistSidebar/wishlistSidebar.selectors";
import { Config } from "@/utils/config.utils";
import { sortBy } from "@/utils/general";

import { WishlistCreateListModalComponent } from "./WishlistCreateListModal/WishlistCreateListModal.component";
import { WishlistListItem } from "./WishlistListItem/WishlistListItem.component";

type WishlistListContainerProps = {
    wishlists: Array<WishlistModel>;
    isMobile: boolean;
};

const WishlistListContainer = ({
    wishlists,
    isMobile
}: WishlistListContainerProps) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();

    const [showCreateListModal, setShowCreateListModal] = useState(false);

    const customer = useSelector(getCustomer);
    const currentListId = useSelector(getCurrentListId);
    const isLoadingCreate = useSelector(WishlistSelectors.isLoadingOne);

    const isLoggedIn = !!(customer && !customer.guest);

    const allowCreateNew = wishlists.length < MAX_WISHLISTS;

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        dispatch(WishlistModule.loadAll());
    }, []);

    const onSelect = (id: string) => {
        dispatch(update({ currentListId: id, visible: !isMobile }));
    };

    const showCreateList = () => {
        setShowCreateListModal(true);
    };

    const onCreate = async (name: string) => {
        const result = await dispatch(createWishlist(name));

        if (result.type === APIStatus.SUCCESS) {
            dispatch(update({ currentListId: result.payload.id }));
        } else {
            setShowCreateListModal(false);
        }

        return result;
    };

    return (
        <>
            {wishlists.length > 0 && (
                <div className="d-flex flex-row flex-wrap">
                    <div className="col d-flex mw-100 flex-column pr-3 pl-0">
                        {wishlists
                            .sort(sortBy("name", Config.getLocale(), false))
                            .map((list) => {
                                return (
                                    <WishlistListItem
                                        key={`listitem-${list.id}`}
                                        list={list}
                                        isSelected={list.id === currentListId}
                                        onSelect={onSelect}
                                    />
                                );
                            })}
                    </div>
                </div>
            )}
            {allowCreateNew && isLoggedIn && (
                <>
                    {wishlists.length > 0 && <hr />}
                    <div className="d-flex flex-row flex-wrap">
                        <div className="col d-flex flex-column pr-3 pl-0 justify-content-start">
                            <div>
                                <Button
                                    type="button"
                                    onClick={showCreateList}
                                    color="link"
                                >
                                    <i className="las la-plus pr-2"></i>
                                    {t("wishlist.container.create")}
                                </Button>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {allowCreateNew && (
                <WishlistCreateListModalComponent
                    open={showCreateListModal}
                    onCreate={onCreate}
                    onCancel={() => setShowCreateListModal(false)}
                    isLoading={isLoadingCreate}
                />
            )}
        </>
    );
};

export { WishlistListContainer };
